// Normalize a `build_plan` tool payload (delivered as a `create_plan`
// suggestion) into a clean, predictable structure for applying to a project.
//
// Defensive on purpose: an AI response can be partial or slightly off-shape
// (missing time/distance, subtasks as strings or objects, unknown status), and
// we'd still rather build what we can than drop the whole plan.
//
// `time`/`distance` are kept as-is when the model provides finite numbers and
// left `undefined` otherwise, so layoutPlan can tell "the AI placed this" from
// "needs a position". distance is the Y-axis stop (a location / distance from
// the goal); time is the X-axis position.

const VALID_STATUS = new Set(['pending', 'active', 'completed', 'blocked'])

export function normalizeBuildPlan(input) {
  const src = input || {}
  const flows = (Array.isArray(src.flows) ? src.flows : [])
    .filter((f) => f && f.name != null && String(f.name).trim())
    .map((f) => ({
      name: String(f.name).trim(),
      color: typeof f.color === 'string' && f.color.trim() ? f.color.trim() : undefined,
      checkpoints: (Array.isArray(f.checkpoints) ? f.checkpoints : [])
        .filter((c) => c && c.name != null && String(c.name).trim())
        .map((c) => ({
          name: String(c.name).trim(),
          time: Number.isFinite(c.time) ? c.time : undefined,
          distance: Number.isFinite(c.distance) ? c.distance : undefined,
          estimatedDuration: Number.isFinite(c.estimatedDuration) ? c.estimatedDuration : undefined,
          status: VALID_STATUS.has(c.status) ? c.status : undefined,
          subtasks: (Array.isArray(c.subtasks) ? c.subtasks : [])
            .map((s) => (typeof s === 'string' ? s : s && s.text))
            .filter((t) => typeof t === 'string' && t.trim())
            .map((t) => t.trim()),
        })),
    }))

  const dependencies = (Array.isArray(src.dependencies) ? src.dependencies : [])
    .filter((d) => d && d.fromCheckpoint != null && d.toCheckpoint != null)
    .map((d) => ({
      fromFlow: d.fromFlow != null ? String(d.fromFlow).trim() : '',
      fromCheckpoint: String(d.fromCheckpoint).trim(),
      toFlow: d.toFlow != null ? String(d.toFlow).trim() : '',
      toCheckpoint: String(d.toCheckpoint).trim(),
    }))

  const totalCheckpoints = flows.reduce((n, f) => n + f.checkpoints.length, 0)
  return { flows, dependencies, totalFlows: flows.length, totalCheckpoints }
}

// Assign chart coordinates to a normalized plan.
//
// distance (Y — the location / distance-from-goal stop):
//   - kept when the AI provided a finite value (this is the meaningful axis,
//     so we always respect an explicit location);
//   - otherwise the flow is dropped onto its own horizontal lane so plans
//     without locations still don't overlap.
//
// time (X — when):
//   - layout 'duration' → spaced by estimatedDuration within each flow;
//   - layout 'even'     → spread evenly within each flow;
//   - layout 'auto'     → kept when every checkpoint in the flow has a finite
//     time, else spread evenly.
//
// `opts`: { timeStart, timeRange, distanceRange, layout }
//   (defaults 0 / 12 / 4 / 'auto'). distanceRange should match the number of
//   Y-axis stops minus one (distanceLabels.length - 1).
const round = (n, d) => {
  const p = 10 ** d
  return Math.round(n * p) / p
}

function evenTimes(n, timeStart, timeRange) {
  if (n <= 0) return []
  return Array.from({ length: n }, (_, i) =>
    round(timeStart + ((i + 1) / (n + 1)) * timeRange, 2)
  )
}

// Time positions spaced by estimatedDuration (each checkpoint sits at the
// midpoint of its duration slice). Missing/zero durations count as 1 so every
// checkpoint still gets a distinct slot; falls back to even if all are zero.
function durationTimes(cps, timeStart, timeRange) {
  const n = cps.length
  if (n === 0) return []
  const durs = cps.map((c) =>
    Number.isFinite(c.estimatedDuration) && c.estimatedDuration > 0 ? c.estimatedDuration : 1
  )
  const total = durs.reduce((a, b) => a + b, 0)
  if (total <= 0) return evenTimes(n, timeStart, timeRange)
  const times = []
  let acc = 0
  for (let i = 0; i < n; i++) {
    times.push(round(timeStart + ((acc + durs[i] / 2) / total) * timeRange, 2))
    acc += durs[i]
  }
  return times
}

export function layoutPlan(plan, opts = {}) {
  const flows = Array.isArray(plan?.flows) ? plan.flows : []
  const timeStart = Number.isFinite(opts.timeStart) ? opts.timeStart : 0
  const timeRange = Number.isFinite(opts.timeRange) && opts.timeRange > 0 ? opts.timeRange : 12
  const distanceRange =
    Number.isFinite(opts.distanceRange) && opts.distanceRange > 0 ? opts.distanceRange : 4
  const layout = opts.layout === 'duration' ? 'duration' : opts.layout === 'even' ? 'even' : 'auto'

  const F = flows.length
  return {
    ...plan,
    flows: flows.map((f, fi) => {
      const cps = f.checkpoints || []
      const lane = round(F > 0 ? ((fi + 1) / (F + 1)) * distanceRange : distanceRange / 2, 3)

      // Time: respect provided times only in 'auto' when the whole flow has
      // them; otherwise spread by the chosen method.
      const allHaveTime = cps.length > 0 && cps.every((c) => Number.isFinite(c.time))
      let times
      if (layout === 'duration') times = durationTimes(cps, timeStart, timeRange)
      else if (layout === 'even') times = evenTimes(cps.length, timeStart, timeRange)
      else times = allHaveTime ? cps.map((c) => c.time) : evenTimes(cps.length, timeStart, timeRange)

      return {
        ...f,
        checkpoints: cps.map((c, ci) => ({
          ...c,
          // Distance: honor an explicit location; otherwise drop on the lane.
          distance: Number.isFinite(c.distance) ? c.distance : lane,
          time: times[ci],
        })),
      }
    }),
  }
}
